import { useEffect, useState } from "react";
import axios from "axios";
import { format } from "date-fns";
import { toast } from "sonner";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../components/ui/table";
import { Badge } from "../components/ui/badge";
import { Button } from "../components/ui/button";
import { RefreshCw, ArrowLeft, Mail, Phone } from "lucide-react";
import { Link } from "react-router-dom";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function Admin() {
  const [items, setItems] = useState([]);
  const [stats, setStats] = useState({ total: 0, new: 0 });
  const [loading, setLoading] = useState(false);
  const [selected, setSelected] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const [a, b] = await Promise.all([
        axios.get(`${API}/estimates`),
        axios.get(`${API}/estimates/stats`),
      ]);
      setItems(a.data);
      setStats(b.data);
    } catch (err) {
      console.error(err);
      toast.error("Failed to load estimates");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const updateStatus = async (id, status) => {
    try {
      await axios.patch(`${API}/estimates/${id}?status=${status}`);
      toast.success(`Marked as ${status}`);
      load();
    } catch (err) {
      toast.error("Failed to update status");
    }
  };

  return (
    <div className="min-h-screen bg-[#F5F5F0]" data-testid="admin-page">
      <header className="border-b border-[#E5E5E0] bg-[#FFFFFF]">
        <div className="max-w-7xl mx-auto px-6 lg:px-10 h-20 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Link
              to="/"
              data-testid="admin-back-link"
              className="inline-flex items-center gap-2 text-[#4A4A46] hover:text-[#1C1C1A] text-sm font-sans-body"
            >
              <ArrowLeft size={16} />
              Back to site
            </Link>
            <span className="font-serif-display text-2xl tracking-tighter text-[#1C1C1A]">
              JSE · Crew Dashboard
            </span>
          </div>
          <Button
            variant="outline"
            onClick={load}
            disabled={loading}
            data-testid="admin-refresh"
            className="gap-2"
          >
            <RefreshCw size={14} className={loading ? "animate-spin" : ""} />
            Refresh
          </Button>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-6 lg:px-10 py-12">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-10">
          <StatCard label="Total Estimates" value={stats.total} testid="stat-total" />
          <StatCard label="New / Unreviewed" value={stats.new} accent testid="stat-new" />
          <StatCard label="In progress" value={stats.total - stats.new} testid="stat-progress" />
        </div>

        <div className="bg-[#FFFFFF] border border-[#E5E5E0]">
          <div className="px-6 py-4 border-b border-[#E5E5E0] flex items-center justify-between">
            <h2 className="font-serif-display text-2xl tracking-tighter text-[#1C1C1A]">
              Estimate Requests
            </h2>
            <span className="font-sans-body text-xs uppercase tracking-[0.2em] text-[#4A4A46]">
              {items.length} total
            </span>
          </div>

          {items.length === 0 ? (
            <div className="px-6 py-20 text-center font-sans-body text-sm text-[#4A4A46]" data-testid="admin-empty">
              No estimates yet. Submissions will appear here.
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Date</TableHead>
                  <TableHead>Name</TableHead>
                  <TableHead>Contact</TableHead>
                  <TableHead>Service</TableHead>
                  <TableHead>Timeframe</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead className="text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {items.map((e) => (
                  <TableRow
                    key={e.id}
                    className="cursor-pointer"
                    onClick={() => setSelected(e)}
                    data-testid={`estimate-row-${e.id}`}
                  >
                    <TableCell className="font-sans-body text-xs">
                      {format(new Date(e.created_at), "MMM d, yyyy · h:mma")}
                    </TableCell>
                    <TableCell className="font-medium">{e.name}</TableCell>
                    <TableCell>
                      <div className="flex flex-col gap-1 text-xs">
                        <a href={`mailto:${e.email}`} className="inline-flex items-center gap-1 text-[#1C1C1A] hover:text-[#CC5500]">
                          <Mail size={12} /> {e.email}
                        </a>
                        <a href={`tel:${e.phone}`} className="inline-flex items-center gap-1 text-[#4A4A46] hover:text-[#CC5500]">
                          <Phone size={12} /> {e.phone}
                        </a>
                      </div>
                    </TableCell>
                    <TableCell className="text-xs">{e.service_type}</TableCell>
                    <TableCell className="text-xs">{e.timeframe || "—"}</TableCell>
                    <TableCell>
                      <Badge variant={e.status === "new" ? "default" : "secondary"}>
                        {e.status}
                      </Badge>
                    </TableCell>
                    <TableCell className="text-right">
                      {e.status === "new" ? (
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={(ev) => {
                            ev.stopPropagation();
                            updateStatus(e.id, "contacted");
                          }}
                          data-testid={`mark-contacted-${e.id}`}
                        >
                          Mark contacted
                        </Button>
                      ) : (
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={(ev) => {
                            ev.stopPropagation();
                            updateStatus(e.id, "new");
                          }}
                          data-testid={`mark-new-${e.id}`}
                        >
                          Re-open
                        </Button>
                      )}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </div>

        {selected && (
          <div
            className="fixed inset-0 bg-[#1C1C1A]/60 z-50 flex items-center justify-center p-6"
            onClick={() => setSelected(null)}
            data-testid="estimate-detail-modal"
          >
            <div
              className="bg-[#F5F5F0] border border-[#E5E5E0] max-w-2xl w-full p-8"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="flex justify-between items-start mb-6">
                <h3 className="font-serif-display text-3xl tracking-tighter text-[#1C1C1A]">
                  {selected.name}
                </h3>
                <button
                  onClick={() => setSelected(null)}
                  className="text-[#4A4A46] hover:text-[#1C1C1A] text-2xl leading-none"
                  data-testid="modal-close"
                >
                  ×
                </button>
              </div>
              <dl className="grid grid-cols-2 gap-4 text-sm font-sans-body">
                <Detail k="Email" v={selected.email} />
                <Detail k="Phone" v={selected.phone} />
                <Detail k="Service" v={selected.service_type} />
                <Detail k="Timeframe" v={selected.timeframe || "—"} />
                <Detail k="Address" v={selected.address || "—"} full />
                <Detail k="Details" v={selected.details || "—"} full />
                <Detail
                  k="Submitted"
                  v={format(new Date(selected.created_at), "PPpp")}
                  full
                />
              </dl>
            </div>
          </div>
        )}
      </main>
    </div>
  );
}

function StatCard({ label, value, accent, testid }) {
  return (
    <div
      className={`p-6 border border-[#E5E5E0] ${accent ? "bg-[#1C1C1A] text-[#F5F5F0]" : "bg-[#FFFFFF]"}`}
      data-testid={testid}
    >
      <div className={`font-sans-body text-[11px] uppercase tracking-[0.25em] ${accent ? "text-[#CC5500]" : "text-[#4A4A46]"}`}>
        {label}
      </div>
      <div className="mt-3 font-serif-display text-5xl tracking-tightest">
        {value}
      </div>
    </div>
  );
}

function Detail({ k, v, full }) {
  return (
    <div className={full ? "col-span-2" : ""}>
      <dt className="text-[11px] uppercase tracking-[0.25em] text-[#4A4A46] mb-1">{k}</dt>
      <dd className="text-[#1C1C1A] whitespace-pre-wrap">{v}</dd>
    </div>
  );
}
